import React, {useState, useEffect} from "react";
import {connect} from "react-redux";
import {DropdownSelect, DropdownSelectText} from "@reside/ui";
import {AppState, select} from "../../store";
import {AdminNavigationHeader} from "../../atoms/admin-navigation-header";
import {AdminActionRow} from "../../atoms/admin-action-row";
import {DocumentTitle} from "../../atoms/document-title";
import {DashboardGrid} from "../../atoms/dashboard-table";
import {DashboardApproveModal} from "../../atoms/dashboard-table/DashboardApproveModal";
import {DashboardArchiveModal} from "../../atoms/dashboard-table/DashboardArchiveModal";
import {DashboardDeleteAdmissionModal} from "../../atoms/dashboard-table/DashboardDeleteAdmissionModal";
import {DashboardForceDeleteAdmissionModal} from "../../atoms/dashboard-table/DashboardForceDeleteAdmissionModal";
import {DashboardReturnToResidentModal} from "../../atoms/dashboard-table/DashboardReturnToResidentModal";
import {
  ResidentLoginModalProvider,
  ResidentLoginModal,
} from "../../atoms/dashboard-table/atoms/resident-login-modal";
import {DashboardModalContextProvider} from "../../atoms/dashboard-table/DashboardModalContext";
import {PccDashboardCheckboxesContextProvider} from "../../atoms/dashboard-table/atoms/pcc-document-upload-modal";
import {Copyright} from "../../atoms/copyright";
import {
  ChangeHistoryModal,
  ChangeHistoryModalProvider,
} from "../../atoms/dashboard-table/atoms/change-history-modal";
import {AdminAdmissionsSearch} from "../../atoms/admin-search";
import {StarFilter} from "../../atoms/star-filter";
import {
  DashboardLayout,
  DashboardTileBleed,
  DashboardTileBoardMax,
  FullWidthDashboardHeader,
  FullWidthDashboardLayoutContent,
} from "./styles";
import {useFetchActiveBrandingTheme} from "../page-admin-organizations-branding/hooks/useFetchActiveBrandingTheme";
import {BackdropSpinner} from "../../atoms/spinner";
import {ListTileViewSwitch} from "./ListTileViewSwitch";
import {
  TILE_VIEW_KEY,
  VIEW_KEYS,
  STATUSES,
  WORKFLOW_FILTERS,
} from "./utils/constants";
import {VIEW_MODE} from "../../atoms/dashboard-table/constants";
import {Status, WorkflowFilter} from "./utils/types";
import {DashboardAdmissionsListView} from "./DashboardAdmissionsListView";
import {isResideAdmin} from "../../utils/helpers/users";
import {useAdmissionsByStatusQueryUtils} from "../../atoms/dashboard-table/hooks";
import {useStarredAdmissionsCount} from "../../atoms/dashboard-table/hooks/useStarredAdmissionsCount";

// Filter section component to reduce nesting
const DashboardFilters = ({
  isTileView,
  onStarFilterChange,
  admissionStatus,
  onStatusSelect,
  starFilterActive,
  workflowFilter,
  onWorkflowFilterSelect,
}: {
  isTileView: boolean;
  onStarFilterChange: (isActive: boolean) => void;
  admissionStatus: Status;
  onStatusSelect: (item: Status) => void;
  starFilterActive: boolean;
  workflowFilter: WorkflowFilter;
  onWorkflowFilterSelect: (item: WorkflowFilter) => void;
}) => (
  <div style={{display: "flex", alignItems: "center", gap: 12}}>
    <StarFilter active={starFilterActive} onFilterChange={onStarFilterChange} />
    <DropdownSelect
      trigger={({isOpened}) => (
        <DropdownSelectText isOpened={isOpened} label={workflowFilter.label} />
      )}
      selectItem={onWorkflowFilterSelect}
      selectedItem={workflowFilter}
      items={WORKFLOW_FILTERS as WorkflowFilter[]}
      label="Workflow"
    />
    {!isTileView && (
      <DropdownSelect
        trigger={({isOpened}) => (
          <DropdownSelectText
            isOpened={isOpened}
            label={admissionStatus.label}
          />
        )}
        selectItem={onStatusSelect}
        selectedItem={admissionStatus}
        items={STATUSES as Status[]}
        label="Status"
      />
    )}
  </div>
);

// Context providers wrapper to reduce nesting
const DashboardContextProviders = ({
  isResideAdmin,
  children,
}: {
  isResideAdmin: boolean;
  children: React.ReactNode;
}) => (
  <DashboardModalContextProvider>
    <PccDashboardCheckboxesContextProvider
      isPccDashboardCheckboxesFeatureEnabled={!isResideAdmin}
    >
      <ResidentLoginModalProvider>
        <ChangeHistoryModalProvider>{children}</ChangeHistoryModalProvider>
      </ResidentLoginModalProvider>
    </PccDashboardCheckboxesContextProvider>
  </DashboardModalContextProvider>
);

// Dashboard header component to reduce nesting
const DashboardHeader = ({
  isTileView,
  admissionStatus,
  onStatusSelect,
  onStarFilterChange,
  starFilterActive,
  workflowFilter,
  onWorkflowFilterSelect,
  view,
  setView,
}: {
  isTileView: boolean;
  admissionStatus: Status;
  onStatusSelect: (item: Status) => void;
  onStarFilterChange: (isActive: boolean) => void;
  starFilterActive: boolean;
  workflowFilter: WorkflowFilter;
  onWorkflowFilterSelect: (item: WorkflowFilter) => void;
  view: (typeof VIEW_KEYS)[number];
  setView: (view: (typeof VIEW_KEYS)[number]) => void;
}) => (
  <FullWidthDashboardHeader>
    <AdminActionRow
      search={<AdminAdmissionsSearch />}
      filter={
        <DashboardFilters
          isTileView={isTileView}
          onStarFilterChange={onStarFilterChange}
          admissionStatus={admissionStatus}
          onStatusSelect={onStatusSelect}
          starFilterActive={starFilterActive}
          workflowFilter={workflowFilter}
          onWorkflowFilterSelect={onWorkflowFilterSelect}
        />
      }
      listTileViewSwitch={<ListTileViewSwitch view={view} setView={setView} />}
    />
  </FullWidthDashboardHeader>
);

// Dashboard content component to reduce nesting
const DashboardContent = ({
  isTileView,
  starFilterActive,
  admissionStatus,
  onStatusSelect,
  workflowType,
}: {
  isTileView: boolean;
  starFilterActive: boolean;
  admissionStatus: Status;
  onStatusSelect: (item: Status) => void;
  workflowType: string | null;
}) => (
  <FullWidthDashboardLayoutContent>
    {isTileView ? (
      <DashboardTileBleed>
        <DashboardTileBoardMax>
          <DashboardGrid
            starredOnly={starFilterActive}
            workflowType={workflowType}
          />
        </DashboardTileBoardMax>
      </DashboardTileBleed>
    ) : (
      <DashboardTileBleed>
        <DashboardTileBoardMax>
          <DashboardAdmissionsListView
            admissionStatus={admissionStatus}
            selectItem={onStatusSelect}
            starFilterActive={starFilterActive}
            workflowType={workflowType}
          />
        </DashboardTileBoardMax>
      </DashboardTileBleed>
    )}
  </FullWidthDashboardLayoutContent>
);

// Dashboard modals component to group modals
const DashboardModals = () => (
  <>
    <DashboardApproveModal />
    <DashboardArchiveModal />
    <DashboardDeleteAdmissionModal />
    <DashboardForceDeleteAdmissionModal />
    <DashboardReturnToResidentModal />
    <ResidentLoginModal />
    <ChangeHistoryModal />
  </>
);

type StateProps = {
  isResideAdmin: boolean;
  organizationIdByUser: string;
  activeFacilitiesIds: ReadonlyArray<string> | null;
  isAllowedToSeeAdmissions: boolean;
};

const PageAdminDashboardRenderer = ({
  isResideAdmin,
  organizationIdByUser,
  activeFacilitiesIds,
}: StateProps) => {
  // Fetch active custom branding theme
  const {isLoading} = useFetchActiveBrandingTheme({
    organizationIdByUser,
  });

  const [view, setView] = useState<(typeof VIEW_KEYS)[number]>(TILE_VIEW_KEY);
  const [admissionStatus, setAdmissionStatus] = useState(STATUSES[0]);
  const [starFilterActive, setStarFilterActive] = useState(false);
  const [workflowFilter, setWorkflowFilter] = useState(WORKFLOW_FILTERS[0]);

  const {refetchAdmissionsByStatusQuery} = useAdmissionsByStatusQueryUtils();
  const {data: totalStarredAdmissions} = useStarredAdmissionsCount();

  const isTileView = view === TILE_VIEW_KEY;

  // Reset star filter when count hits 0
  useEffect(() => {
    if (
      starFilterActive &&
      (!totalStarredAdmissions || totalStarredAdmissions === 0)
    ) {
      setStarFilterActive(false);
    }
  }, [starFilterActive, totalStarredAdmissions]);

  // Refetch admissions when star/unstar happens and star filter is active
  useEffect(() => {
    const handleAdmissionStarToggled = () => {
      // Refetch all status queries with starredOnly filter
      Object.values(STATUSES).forEach(status => {
        refetchAdmissionsByStatusQuery({
          status: status.id as any,
          facilityIds: activeFacilitiesIds,
          starredOnly: starFilterActive,
          viewMode: isTileView ? VIEW_MODE.GRID : VIEW_MODE.LIST,
          workflowType: workflowFilter.id,
        });
      });
    };

    window.addEventListener("admissionStarToggled", handleAdmissionStarToggled);
    return () => {
      window.removeEventListener(
        "admissionStarToggled",
        handleAdmissionStarToggled,
      );
    };
  }, [
    starFilterActive,
    activeFacilitiesIds,
    refetchAdmissionsByStatusQuery,
    isTileView,
    workflowFilter.id,
  ]);

  const selectItem = (item: Status) => {
    if (admissionStatus === item) return;
    setAdmissionStatus(item);
  };

  const selectWorkflowFilter = (item: WorkflowFilter) => {
    if (workflowFilter === item) return;
    setWorkflowFilter(item);
  };

  return (
    <BackdropSpinner active={isLoading}>
      <DashboardLayout>
        <DocumentTitle title="Reside - Admin - Dashboard" />
        <AdminNavigationHeader />
        <DashboardContextProviders isResideAdmin={isResideAdmin}>
          <DashboardHeader
            isTileView={isTileView}
            admissionStatus={admissionStatus}
            onStatusSelect={selectItem}
            onStarFilterChange={setStarFilterActive}
            starFilterActive={starFilterActive}
            workflowFilter={workflowFilter}
            onWorkflowFilterSelect={selectWorkflowFilter}
            view={view}
            setView={setView}
          />
          <DashboardContent
            isTileView={isTileView}
            starFilterActive={starFilterActive}
            admissionStatus={admissionStatus}
            onStatusSelect={selectItem}
            workflowType={workflowFilter.id}
          />
          <DashboardModals />
        </DashboardContextProviders>
        <Copyright />
      </DashboardLayout>
    </BackdropSpinner>
  );
};

const mapStateToProps = (
  state: AppState & {adminSession: {user: {organization: {id: string}}}},
): StateProps => {
  const {facilities} = select.adminSession.user(state);
  return {
    isResideAdmin: select.adminSession.isResideAdmin(state),
    organizationIdByUser: state?.adminSession?.user?.organization?.id,
    activeFacilitiesIds: select.adminSession.activeFacilitiesIds(state),
    isAllowedToSeeAdmissions:
      Boolean(facilities.length) ||
      isResideAdmin(select.adminSession.user(state)),
  };
};

export const PageAdminDashboard = connect(mapStateToProps)(
  PageAdminDashboardRenderer,
);
